import units from "utils/units"
import { BaseComponent } from "utils/base-component"
import { floor, round } from "utils/helpers"

import "./index.scss"

const TEMP_STEPS = {
  standard: [-20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 40],
  cold: [-40, -35, -30, -25, -20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40],
} as const

type Variant = keyof typeof TEMP_STEPS

type Config = {
  value: number
  showUnits?: boolean
  precise?: boolean
  variant?: Variant
}

export class TempValue extends BaseComponent<Config> {
  static cssClass = "temp-value"
  static defaultVariant: Variant = "standard"

  target!: Element
  value?: number
  showUnits!: boolean
  precise!: boolean
  variant!: Variant

  static parse(temp: string, root?: HTMLElement) {
    if (temp === "" || temp === "-") return undefined

    const value = parseFloat(temp)
    if (Number.isNaN(value)) return undefined

    const c = TempValue.for({ value }, root)
    c.update(value)

    return c
  }

  init() {
    const value = this.config?.value ?? parseFloat(this.root.dataset.value!)
    this.value = Number.isNaN(value) ? undefined : value
    this.showUnits = this.config?.showUnits || this.root.dataset?.units !== undefined
    this.precise = this.config?.precise || this.root.dataset.precise !== undefined
    this.variant =
      this.config?.variant || (this.root.dataset?.variant as Variant) || TempValue.defaultVariant
    if (this.variant) this.toggleModifier(this.variant, true)
    if (this.value !== undefined) this.toggleModifier(this.tempStep(this.value), true)

    this.target = this.root.tagName === "g" ? this.root.querySelector("text")! : this.root

    this.render()
    units.onChange(() => this.render())
  }

  update(value: number) {
    const oldStep = this.value !== undefined ? this.tempStep(this.value) : undefined
    const newStep = this.tempStep(value)
    if (newStep !== oldStep) {
      if (oldStep) this.toggleModifier(oldStep, false)
      this.toggleModifier(newStep, true)
    }

    this.value = value
    this.render()
  }

  render() {
    const formatted =
      this.value === undefined
        ? "—"
        : units.formatTemp(this.value, {
            withUnits: false,
            precision: this.precise ? 1 : 0,
          })

    // SVG cells (e.g. freezing graph) have a single <text> node
    if (this.root.tagName === "g") {
      this.target.textContent =
        this.showUnits && this.value !== undefined
          ? `${formatted}${units.formatTempUnits()}`
          : formatted
      return
    }

    this.element("number")?.setText(formatted)
    this.element("units")?.setText(units.formatTempUnits().replace("\u00B0", ""))
  }

  private tempStep(value: number) {
    const steps = TEMP_STEPS[this.variant]
    const v = this.precise ? floor(value) : round(value)
    let step = steps.findIndex((s) => v < s)
    if (step === -1) step = steps.length - 1
    return `${step + 1}`
  }
}
